<script setup lang="ts">
import {
  UIButton,
  UIIcon,
  UIInputToggleRadio,
  UITag,
} from '@groover-dev/groover-ui'

import { vConvertToTargetCurrency } from '~/directives/ConvertToTargetCurrency'

import CurrencyOrder from '~/components/shared/currency/order.vue'
import CurrencySwapper from '~/components/shared/currency/swapper.vue'

import { provideFetchPlanCheckout } from '~/api-core/Subscription/Plan'

import type { Plan } from '~/types/Subscription'

type Props = {
  theme?: 'light' | 'dark'
  learnMoreUrl?: string | null
  learnMoreText?: string | null
}

const {
  theme = 'dark',
  learnMoreUrl = null,
  learnMoreText = null,
} = defineProps<Props>()

const { t } = useI18n()
const { coreFetch } = useProvideCoreFetch()
const stripeCheckoutModalClientSecret = useState<string | undefined>(
  'stripeCheckoutModalClientSecret',
  () => undefined,
)

const { plans, selectedPlanId } = storeToRefs(useSubscriptionStore())
const { FETCH_PLANS, SET_SELECTED_PLAN_ID } = useSubscriptionStore()

await callOnce('FETCH_PLANS', FETCH_PLANS)

const plan = computed(() =>
  plans.value.find((p: Plan) => p.id === selectedPlanId.value),
)

type ToggleRadioOption<T> = { label: string; value: T }

const options = computed<ToggleRadioOption<number>[]>(() =>
  plans.value.map((p: Plan) => ({
    label: t(`subscription.interval.${p.interval}`),
    value: p.id,
  })),
)

const textColorClass = computed(() =>
  theme === 'dark' ? 'tw-text-inverse' : 'tw-text-color-default',
)
const bgColorClass = computed(() =>
  theme === 'dark' ? 'tw-bg-[#131313]' : 'tw-bg-fill',
)
const benefitsBgClass = computed(() =>
  theme === 'dark' ? 'tw-bg-fill-alt' : 'tw-bg-surface',
)

function getMonthlyPrice(plan: Plan) {
  const INTERVAL_MULTIPLIERS = {
    year: 1 / 12,
    day: 30,
    week: 4,
    month: 1,
  } as const

  return plan.price * (INTERVAL_MULTIPLIERS[plan.interval] ?? 1)
}

async function subscribeSelectedPlan() {
  const fetchPlanCheckout = provideFetchPlanCheckout(coreFetch)

  const { client_secret: clientSecret } = await fetchPlanCheckout({
    planId: selectedPlanId.value,
    returnUrl: location.href,
  })

  stripeCheckoutModalClientSecret.value = clientSecret
}
</script>

<template>
  <div
    v-if="plans.length > 0 && plan"
    class="tw-flex tw-flex-col tw-rounded-md tw-p-xs"
    :class="bgColorClass"
  >
    <div class="tw-flex tw-flex-col tw-gap-xl tw-p-xl">
      <div>
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-xl">
          <span class="tw-text-h3" :class="textColorClass">
            {{ t(`subscription.plan.groover-plus-${plan.interval}ly`) }}
          </span>

          <UITag
            v-if="plan.interval === 'year'"
            :text="t('subscription.pricingCard.savePercent')"
            color="accent"
            class="tw-whitespace-nowrap"
            size="sm"
          />
        </div>
        <div
          class="tw-text-h5 tw-flex tw-items-baseline tw-gap-2xs"
          :class="textColorClass"
        >
          <span>
            <CurrencyOrder class="tw-inline-block"
              ><span
                v-convert-to-target-currency.noRounding="
                  getMonthlyPrice(plan)
                " /></CurrencyOrder
            >/{{ t(`common.month`) }}
          </span>
          <CurrencySwapper class="tw-leading-6" color="accent" :theme="theme" />
        </div>
        <span class="tw-text-body-sm tw-text-discrete3">
          <i18n-t
            v-if="plan.interval === 'year'"
            keypath="subscription.pricingCard.billedAnnually"
          >
            <template #price
              ><CurrencyOrder class="tw-inline-block"
                ><span
                  v-convert-to-target-currency.noRounding="
                    plan.price
                  " /></CurrencyOrder
            ></template>
          </i18n-t>
          <!-- To keep the layout consistent -->
          <span v-else class="tw-text-body-sm">&nbsp;</span>
        </span>
      </div>
      <UIInputToggleRadio
        id="subscription-plan-toggle"
        class="tw-self-center"
        :model-value="selectedPlanId"
        :options="options"
        :theme="theme"
        @update:model-value="SET_SELECTED_PLAN_ID($event as number)"
      />
    </div>
    <div class="tw-flex tw-flex-col tw-gap-xl tw-p-xl" :class="benefitsBgClass">
      <ul class="tw-flex tw-flex-col tw-gap-md">
        <li
          v-for="[benefit, enabled] in Object.entries(plan.benefits)"
          :key="benefit"
          class="tw-inline-flex tw-items-center tw-gap-xs"
          :class="enabled ? textColorClass : 'tw-text-discrete2'"
        >
          <UIIcon
            size="sm"
            :name="enabled ? 'mdi:check' : 'mdi:close'"
            :class="
              enabled
                ? theme === 'light'
                  ? 'tw-text-accent'
                  : 'tw-text-inverse-accent'
                : 'tw-text-discrete2'
            "
          />
          {{ t(`subscription.pricingCard.benefits.${benefit}`) }}
        </li>
      </ul>
      <UIButton
        :text="t(`subscription.pricingCard.CTAStartWith.${plan.interval}`)"
        color="accent"
        @click="subscribeSelectedPlan"
      />
      <UIButton
        v-if="learnMoreText && learnMoreUrl"
        :text="learnMoreText"
        :color="theme === 'dark' ? 'alt-accent' : 'accent'"
        variant="outlined"
        :to="learnMoreUrl"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(label.tw-bg-fill-alt) {
  background-color: #131313;
}
</style>
